/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Regions", description = "You can configure Hightouch to run in multiple regions around the world.", lastUpdatedDate = new Date(1784073600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Regions",
        description: "You can configure Hightouch to run in multiple regions around the world.",
        lastUpdatedDate: "Wed Jul 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported regions",
          "depth": 0,
          "id": "supported-regions"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Data in transit",
          "depth": 1,
          "id": "data-in-transit"
        }, {
          "value": "Data at rest",
          "depth": 1,
          "id": "data-at-rest"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Your Hightouch region defines where we host the Hightouch ", _jsx(_components.em, {
          children: "data plane"
        }), ", which is the component of our system that directly interacts with your ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "sources"
        }), " and ", _jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: "destinations"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-regions",
        children: "Supported regions"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch operates in the following Amazon Web Services, Google Cloud Platform, and Azure regions:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Cloud"
            }), _jsx(_components.th, {
              children: "Region"
            }), _jsx(_components.th, {
              align: "left",
              children: "Location"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "AWS"
            }), _jsx(_components.td, {
              children: "ap-south-1"
            }), _jsx(_components.td, {
              align: "left",
              children: "Mumbai, India"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "AWS"
            }), _jsx(_components.td, {
              children: "ap-southeast-2"
            }), _jsx(_components.td, {
              align: "left",
              children: "Sydney, Australia"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "AWS"
            }), _jsx(_components.td, {
              children: "eu-west-1"
            }), _jsx(_components.td, {
              align: "left",
              children: "Ireland"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "AWS"
            }), _jsx(_components.td, {
              children: "us-east-1"
            }), _jsx(_components.td, {
              align: "left",
              children: "N. Virginia, USA"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Azure"
            }), _jsx(_components.td, {
              children: "East US"
            }), _jsx(_components.td, {
              align: "left",
              children: "N. Virginia, USA"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "GCP"
            }), _jsx(_components.td, {
              children: "europe-west1"
            }), _jsx(_components.td, {
              align: "left",
              children: "Belgium"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "GCP"
            }), _jsx(_components.td, {
              children: "me-central2"
            }), _jsx(_components.td, {
              align: "left",
              children: "Dammam, Saudi Arabia"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "GCP"
            }), _jsx(_components.td, {
              children: "us-east4"
            }), _jsx(_components.td, {
              align: "left",
              children: "N. Virginia, USA"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You may prefer to use a particular region for a variety of reasons, including data residency requirements, availability of features like ", _jsx(_components.a, {
          href: "/security/aws/privatelink",
          children: "AWS PrivateLink"
        }), " or ", _jsx(_components.a, {
          href: "/security/gcp/private-service-connect",
          children: "Private Service Connect"
        }), ", or use of a self-managed storage bucket in Amazon S3, Google Cloud Storage."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "When creating a new workspace, you must select your desired region. Once a workspace has been created, its region can't be changed."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "security/region-1-setup.png",
          alt: "Selecting a region from the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "data-in-transit",
        children: "Data in transit"
      }), "\n", _jsx(_components.p, {
        children: "During syncs, your data flows through Hightouch's data plane on its way to your destinations."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "welcome/source-to-destination.png",
          alt: "Hightouch syncs data from sources to destinations"
        })
      }), "\n", _jsx(_components.p, {
        children: "During the in-transit phase of a sync, your data remains within your workspace region. However, Hightouch can't verify where your destinations store your data. If your destination's data region is important to you, verify it with the destination directly."
      }), "\n", _jsx(_components.h3, {
        id: "data-at-rest",
        children: "Data at rest"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only stores data needed to perform ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture-cdc",
          children: "change data capture"
        }), " and ", _jsx(_components.a, {
          href: "/security/storage#observability-and-debugging",
          children: "debugging"
        }), ". By default, Hightouch stores this data in an encrypted Amazon S3 bucket, Google Cloud Storage bucket, or Azure Storage account, depending on the cloud in which your workspace is created. Your Hightouch bucket, and all access to that bucket, is scoped to your region, ensuring compliance with data residency regulations."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch customers on the ", _jsx(_components.a, {
          href: "https://hightouch.com/pricing",
          children: "Business tier"
        }), " can set up an ", _jsx(_components.a, {
          href: "/security/storage",
          children: "external storage bucket"
        }), ", granting more control over their data at rest. If you use an external storage bucket, Hightouch doesn't validate that the bucket is hosted in the same region as your Hightouch workspace."]
      })]
    });
  }
}
export default MDXContent;
